/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";

// owner
import areaClient from "@apis/areaClient";
import { svgIcon } from "@/components/Leaflet/Map/Marker/SvgIcon";
import GeozoneIconDefault from "@images/default/geozone_icon_default.svg";
import {
  PruneCluster,
  PruneClusterForLeaflet,
} from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import { ClusterIcon } from "@/components/Leaflet/Map/Marker/ClusterIcon";

interface GeozoneLayerOptions {
  ignoreGeozoneId?: number;
  floor?: number | null; // null = all floors
}

const GeozoneLayerClass = (L.FeatureGroup as any).extend({
  options: {},
  bounds: L.latLngBounds([]),
  pruneCluster: null as any,

  updateData: function (orgIds: string, options?: GeozoneLayerOptions) {
    const self = this;
    const polygons: any[] = [];

    // Initialize PruneCluster if not already done
    if (!self.pruneCluster) {
      self.pruneCluster = new PruneClusterForLeaflet(120, 20);

      // Set cluster to use shadowPane for lower z-index
      self.pruneCluster.options.pane = "shadowPane";

      PruneCluster.Cluster.ENABLE_MARKERS_LIST = true;
      self.pruneCluster.BuildLeafletClusterIcon = function (cluster: any) {
        const iconClassName = "cluster-svg-geozone";
        const markers = cluster.GetClusterMarkers();
        const _length = markers.length;

        let width = 24,
          textSize = 11;
        if (_length >= 500 && _length < 5000) {
          width = 36;
          textSize = 7;
        } else if (_length >= 5000) {
          width = 44;
          textSize = 6;
        }

        const svgString = `<svg width="${width}" height="${width}" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path id="cluster-bg" fill-rule="evenodd" clip-rule="evenodd" d="M11 0L21.7999 6V18L11 24L0.199951 18V6L11 0Z" fill="#37A857"></path>
            <text id="cluster-text" x="50%" y="50%" font-size="${textSize}" font-weight="bold" transform="translate(0, 3.5)" text-anchor="middle" fill="#ffffff">${_length}</text>
            </svg>
        `;
        return ClusterIcon({
          count: _length,
          svgString: svgString,
          className: iconClassName,
        });
      };

      self.pruneCluster.PrepareLeafletMarker = function (
        leafletMarker: any,
        data: any
      ) {
        leafletMarker.setIcon(data.icon);

        // Set marker to use shadowPane for lower z-index
        leafletMarker.options.pane = "shadowPane";

        if (data.tooltip) {
          leafletMarker.unbindTooltip();
          leafletMarker.bindTooltip(data.tooltip, {
            permanent: true,
            direction: "top",
            className: "transparent-tooltip",
            offset: [0, -8],
          });
        }
        leafletMarker.deviceInfo = data.deviceInfo;
      };

      // Add cluster layer to map if map is available
      if (self._map) {
        self._map.addLayer(self.pruneCluster);
      }
    }

    void areaClient.getAreaByOrgs(orgIds).then(
      ({ data }: { data: any[] }) => {
        self.clearLayers();
        const aggregatedTooltip = L.tooltip({
          sticky: true,
          offset: [0, -4],
        });

        // Filter by floor if specified (null or undefined means all floors)
        const filteredData = options?.floor === null || options?.floor === undefined
          ? data
          : data.filter((item: any) => item.floor === options.floor);

        const geozones = filteredData.sort((a: any, b: any) =>
          a.surface > b.surface ? -1 : 1
        );

        // Clear existing markers from cluster
        self.pruneCluster.RemoveMarkers();

        for (let i = 0; i < geozones.length; i++) {
          const obj = geozones[i];

          if (
            obj.hideOnMap ||
            (options && options.ignoreGeozoneId != null && +options.ignoreGeozoneId === obj.id)
          ) {
            continue;
          }

          let geozone: any = null;
          let centerPoint: [number, number] | null = null;

          if (obj.circleGeozone) {
            geozone = L.circle(
              [obj.circleGeozone.point.x, obj.circleGeozone.point.y],
              {
                pane: "geozonePane",
                color: obj.borderColor,
                weight: 1,
                fillColor: obj.contentColor,
                radius: obj.circleGeozone.radius,
                _type: "CIRCLE",
                name: obj.name,
              } as any
            );
            centerPoint = [
              obj.circleGeozone.point.x,
              obj.circleGeozone.point.y,
            ];
            // .bindTooltip(obj.name, { sticky: true, offset: [0, -4] });
            self.bounds.extend([
              obj.circleGeozone.point.x,
              obj.circleGeozone.point.y,
            ]);
          } else {
            const coordinates = obj.coordinates || [];
            const path: [number, number][] = [];
            for (let j = 0, len = coordinates.length; j < len; j++) {
              path.push([coordinates[j].x, coordinates[j].y]);
            }
            geozone = L.polygon(path, {
              pane: "geozonePane",
              weight: 1,
              fillColor: obj.contentColor,
              color: obj.borderColor,
              name: obj.name,
            } as any);
            // Calculate center point for polygon
            const polygon = L.polygon(path);
            const center = polygon.getBounds().getCenter();
            centerPoint = [center.lat, center.lng];
            // .bindTooltip(obj.name, { sticky: true, offset: [0, -4] });
            self.bounds.extend(path);
          }

          // Create geozone icon marker and add to cluster
          if (centerPoint) {
            const icon = svgIcon({
              iconUrl: obj.icon
                ? obj.icon.includes("?")
                  ? obj.icon + "&includeMapTemplate=true"
                  : obj.icon + "?includeMapTemplate=true"
                : GeozoneIconDefault,
              id: obj.id,
              state: "geozone",
              iconAnchor: [10, 30],
            });
            const marker = new PruneCluster.Marker(
              centerPoint[0],
              centerPoint[1],
              {
                icon: icon,
                tooltip: obj.name ?? "",
              }
            );
            marker.data.deviceInfo = obj;
            self.pruneCluster.RegisterMarker(marker);
          }

          // Add mousemove event listener to show tooltip for each overlapping polygon
          geozone.on("mousemove", (e: any) => {
            const latlng = e.latlng;
            const overlappingPolygons = self.findPolygonsContainPoint(
              latlng,
              polygons
            );
            const titles = overlappingPolygons.map(
              (polygon: any) => polygon.options.name
            );
            self.updateAggregatedTooltip(titles, latlng, aggregatedTooltip);
            self.addLayer(aggregatedTooltip);
          });

          // Add mouseout event listener to hide all tooltips
          geozone.on("mouseout", () => {
            self.removeLayer(aggregatedTooltip);
          });

          polygons.push(geozone);
          self.addLayer(geozone);
        }

        // Process the cluster view
        self.pruneCluster.ProcessView();
      },
      () => {
        // setLoading(false);
      }
    );
  },

  onAdd: function (map: L.Map) {
    this._map = map;
    if (this.pruneCluster) {
      map.addLayer(this.pruneCluster);
    }
    L.FeatureGroup.prototype.onAdd.call(this, map);
  },

  onRemove: function (map: L.Map) {
    this._map = null;
    if (this.pruneCluster) {
      map.removeLayer(this.pruneCluster);
    }
    L.FeatureGroup.prototype.onRemove.call(this, map);
  },

  pointInsidePolygon: function (point: L.LatLng, polygon: any): boolean {
    let inside = false;
    const x = point.lat;
    const y = point.lng;

    if (polygon instanceof L.Circle) {
      return polygon.getBounds().contains(point); // For circles, use bounds.contains
    }

    for (let ii = 0; ii < polygon.getLatLngs().length; ii++) {
      const polyPoints = polygon.getLatLngs()[ii];
      for (
        let i = 0, j = polyPoints.length - 1;
        i < polyPoints.length;
        j = i++
      ) {
        const xi = polyPoints[i].lat,
          yi = polyPoints[i].lng;
        const xj = polyPoints[j].lat,
          yj = polyPoints[j].lng;

        const intersect =
          yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
      }
    }

    return inside;
  },

  updateAggregatedTooltip: function (
    titles: string[],
    latlng: L.LatLng,
    aggregatedTooltip: L.Tooltip
  ) {
    const tooltipContent = titles.join("<br>"); // Join titles with line breaks
    aggregatedTooltip.setContent(tooltipContent);
    aggregatedTooltip.setLatLng(latlng);
  },

  findPolygonsContainPoint: function (latlng: L.LatLng, polygons: any[]): any[] {
    const overlappingPolygons: any[] = [];

    polygons.forEach((polygon) => {
      if (this.pointInsidePolygon(latlng, polygon)) {
        overlappingPolygons.push(polygon);
      }
    });

    return overlappingPolygons;
  },

  getLayer: function (): any {
    return this;
  },

  getBounds: function (): L.LatLngBounds {
    return this.bounds;
  },
});

(L as any).GeozoneLayer = GeozoneLayerClass;
(L as any).geozoneLayer = function (opts?: L.LayerOptions): any {
  return new GeozoneLayerClass(opts);
};

export const GeozoneLayer = (L as any).geozoneLayer;
